'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Cloud, Plus } from 'lucide-react';
import { useDropzone, type FileRejection } from 'react-dropzone';

import { Label } from '@kit/ui/label';
import { toast } from '@kit/ui/sonner';
import { Spinner } from '@kit/ui/spinner';
import { cn } from '@kit/ui/utils';

import {
  useMediaUpload,
  type MediaFile,
  type UploadResult,
  type UploadTranslations,
} from '../hooks/use-media-upload';
import {
  buildAcceptAttribute,
  buildAcceptFromMimeTypes,
  describeMimeTypes,
  UPLOADER_PRESETS,
  type UploaderPreset,
} from '../lib/constants';
import {
  MediaCropDialog,
  type MediaCropDialogTranslations,
} from './media-crop-dialog';
import {
  MediaLightbox,
  type MediaLightboxTranslations,
} from './media-lightbox';
import { MediaThumb } from './media-thumb';

/**
 * UI translations for the MediaUploader component
 */
export interface MediaUploaderTranslations extends UploadTranslations {
  dropHere?: string;
  dragSingle?: string;
  dragMultiple?: string;
  uploading?: string;
  maxFilesReached?: string;
  addMore?: string;
  delete?: string;
  reorderSaved?: string;
  reorderFailed?: string;
  /** Shown when a picked file exceeds maxFileSize. `{maxSize}` is replaced. */
  fileTooLarge?: string;
  /** Shown when a picked file's type is not in `accept`. `{allowed}` is replaced. */
  fileInvalidType?: string;
  /** Accessible label for the hidden single-file input. */
  uploadFileAriaLabel?: string;
  /** Accessible label for the hidden multi-file input. */
  uploadFilesAriaLabel?: string;
  lightbox?: MediaLightboxTranslations;
  crop?: MediaCropDialogTranslations;
}

const DEFAULT_UI_TRANSLATIONS: Required<
  Omit<MediaUploaderTranslations, 'lightbox' | 'crop'>
> = {
  dropHere: 'Drop files here',
  dragSingle: 'Drag a file here or click to upload',
  dragMultiple: 'Drag files here or click to upload',
  uploading: 'Uploading...',
  maxFilesReached:
    'Maximum {maxFiles} files reached. Delete files to add more.',
  uploadSuccess: 'File uploaded successfully',
  uploadError: 'Upload failed',
  deleteSuccess: 'File removed',
  deleteError: 'Failed to delete file',
  invalidFile: 'Invalid file',
  noStoragePath: 'Upload failed: No storage path returned',
  addMore: 'Add more',
  delete: 'Delete',
  reorderSaved: 'Order saved',
  reorderFailed: 'Could not save the new order',
  fileTooLarge: 'This file is too large. Maximum size is {maxSize}.',
  fileInvalidType: 'This file type is not supported. Allowed: {allowed}.',
  uploadFileAriaLabel: 'Upload file',
  uploadFilesAriaLabel: 'Upload files',
};

/**
 * Props for the MediaUploader component
 */
export interface MediaUploaderProps {
  /** Upload mode: single file or multiple files */
  mode: 'single' | 'multiple';

  /** Use a preset configuration, or provide custom options */
  preset?: UploaderPreset;

  /** Custom allowed MIME types (overrides preset) */
  allowedTypes?: string[];

  /** Custom max file size in bytes (overrides preset) */
  maxFileSize?: number;

  /**
   * Accept a browser-unlabelled file whose EXTENSION matches `allowedTypes`
   * (BAL-747). Off by default — only enable it where the server action behind
   * `uploadAction` applies the same fallback, or the user picks a file the
   * client accepted and the server then refuses.
   */
  allowExtensionFallback?: boolean;

  /** Max number of files for multiple mode (overrides preset) */
  maxFiles?: number;

  /** Initial files to display */
  value?: MediaFile[];

  /** Callback when files change */
  onChange?: (files: MediaFile[]) => void;

  /** Callback when a single file is uploaded (for single mode) */
  onUploadComplete?: (file: MediaFile) => void;

  /** Callback when a file is deleted */
  onDelete?: (fileId: string) => void;

  /**
   * Callback when files are reordered (multiple mode only).
   * Receives the new ordered array (already optimistically applied).
   * Return a Promise so the component can show a saved/failed toast.
   */
  onReorder?: (files: MediaFile[]) => Promise<void> | void;

  /** Server action for uploading files */
  uploadAction: (formData: FormData) => Promise<UploadResult>;

  /** Server action for deleting files (optional) */
  deleteAction?: (params: { mediaId: string }) => Promise<UploadResult>;

  /** Additional form data to send with each upload */
  additionalData?: Record<string, string>;

  /** Label for the uploader */
  label?: string;

  /** Whether the field is required */
  required?: boolean;

  /** Hint text shown below the uploader */
  hint?: string;

  /** Whether the uploader is disabled */
  disabled?: boolean;

  /** Additional CSS classes */
  className?: string;

  /** Custom translations for UI text and toast messages */
  translations?: MediaUploaderTranslations;

  /**
   * When set (single mode), picking an image opens a fixed-aspect reframe
   * dialog before upload; the ORIGINAL file is uploaded unchanged and the
   * chosen crop rect + intrinsic dimensions ride along as a `crop` field on
   * the upload's additional data (JSON). Pass `4 / 5` for the dancer headshot.
   */
  cropAspect?: number;
}

/**
 * Stable identity for the uncontrolled default of `value`.
 *
 * MUST NOT be inlined as `value = []`. A literal default allocates a NEW array
 * on every render, and `value` is a dependency of the seeding effect below —
 * so an uncontrolled `<MediaUploader />` (no `value` prop) ran: new [] → dep
 * changed → effect → setInitialFiles → setFiles → re-render → new [] → … an
 * unbounded passive-effect loop that exhausted the heap. React does not raise
 * "Maximum update depth exceeded" for it because the update originates in a
 * passive effect rather than a synchronous render cascade, so it presented as
 * a raw OOM with no React error. Found 2026-07-19 by wiring the previously
 * dark `__tests__/media-uploader-max-size.test.tsx` into CI.
 */
const EMPTY_FILES: MediaFile[] = [];

/**
 * Unified media uploader component
 *
 * - Square aspect-ratio thumbnails in a grid (or single tile)
 * - Click thumb → fullscreen lightbox preview, with Replace + Delete
 * - Hover/focus reveals the per-tile delete button
 * - Drag-to-reorder via @dnd-kit (multiple mode)
 * - Per-tile upload state, never a global modal overlay
 */
export const MediaUploader = React.memo(function MediaUploader({
  mode,
  preset,
  allowedTypes: customAllowedTypes,
  maxFileSize: customMaxFileSize,
  allowExtensionFallback = false,
  maxFiles: customMaxFiles,
  value = EMPTY_FILES,
  onChange,
  onUploadComplete,
  onDelete,
  onReorder,
  uploadAction,
  deleteAction,
  additionalData,
  label,
  required,
  hint,
  disabled,
  className,
  translations: customTranslations,
  cropAspect,
}: MediaUploaderProps) {
  const t = { ...DEFAULT_UI_TRANSLATIONS, ...customTranslations };

  const presetConfig = preset ? UPLOADER_PRESETS[preset] : undefined;
  const allowedTypes = useMemo(
    () => customAllowedTypes ?? presetConfig?.allowedTypes ?? [],
    [customAllowedTypes, presetConfig],
  );
  // Fall back to "no client-side limit" (the server still enforces real
  // limits) when neither a preset nor an explicit size is given. Defaulting to
  // 0 here is a footgun: react-dropzone treats `maxSize: 0` as "reject every
  // file > 0 bytes", so an unconfigured uploader silently rejects everything
  // with a blank "Maximum size is ." message (BAL-338).
  const maxFileSize =
    customMaxFileSize ?? presetConfig?.maxFileSize ?? Number.POSITIVE_INFINITY;
  const maxFiles =
    mode === 'single' ? 1 : (customMaxFiles ?? presetConfig?.maxFiles ?? 10);
  // Native picker restriction: prefer the preset's explicit accept map, else
  // derive one from allowedTypes so a caller passing only `allowedTypes` (e.g.
  // the GDO apply wizard from a stored application_structure field) still greys
  // out disallowed files in the OS dialog — not just rejects them post-pick.
  const accept = useMemo(
    () => presetConfig?.accept ?? buildAcceptFromMimeTypes(allowedTypes),
    [presetConfig, allowedTypes],
  );

  // Human-readable descriptors derived from maxFileSize / accept so hints and
  // rejection toasts always track the configured limits — bumping maxFileSize
  // updates the displayed size everywhere, no hardcoded "max 5MB" to drift.
  const maxSizeLabel = Number.isFinite(maxFileSize)
    ? `${Math.round(maxFileSize / (1024 * 1024))} MB`
    : '';
  const allowedExtLabel =
    describeMimeTypes(allowedTypes) ??
    (accept ? Object.values(accept).flat().join(', ') : '');

  // Hint precedence: explicit prop → preset hint → a label derived from the
  // allowed MIME types, so the allowed formats are always shown even when the
  // caller supplies only `allowedTypes` and no preset/hint. Size is appended
  // below from maxFileSize.
  const baseHint =
    hint ?? presetConfig?.hint ?? describeMimeTypes(allowedTypes);
  const hintText = baseHint
    ? maxSizeLabel
      ? `${baseHint}, max ${maxSizeLabel}`
      : baseHint
    : maxSizeLabel
      ? `Max ${maxSizeLabel}`
      : undefined;

  const {
    files,
    isPending,
    deletingId,
    canAddMore,
    uploadFile,
    deleteFile,
    setInitialFiles,
  } = useMediaUpload({
    allowedTypes,
    maxFileSize,
    allowExtensionFallback,
    maxFiles,
    uploadAction,
    deleteAction,
    onUploadComplete,
    onDeleteComplete: onDelete,
    translations: {
      uploadSuccess: t.uploadSuccess,
      uploadError: t.uploadError,
      deleteSuccess: t.deleteSuccess,
      deleteError: t.deleteError,
      invalidFile: t.invalidFile,
      noStoragePath: t.noStoragePath,
    },
  });

  // Lightbox state
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Reframe state: the picked image awaiting a fixed-aspect crop before upload
  // (only when `cropAspect` is set). Null = dialog closed.
  const [cropFile, setCropFile] = useState<File | null>(null);

  // Track the last `value` reference we seeded from so we can re-seed when the
  // parent supplies a genuinely new `value` (e.g. server data resolves after
  // first paint, or a form reset) without looping on the `files` → onChange →
  // value echo. We compare by identity: our own onChange emits the internal
  // `files` array, which the parent typically does NOT feed back as the same
  // reference into `value`, but even if it did, the identity guard below skips
  // re-seeding when the incoming `value` is the array we last emitted.
  const lastSeededValueRef = useRef<MediaFile[] | undefined>(undefined);
  const lastEmittedFilesRef = useRef<MediaFile[] | undefined>(undefined);

  // Sync external `value` into internal state whenever its identity changes,
  // skipping the self-induced echo (when the parent mirrors our emitted files
  // straight back into `value`).
  useEffect(() => {
    if (value === lastSeededValueRef.current) return;
    if (value === lastEmittedFilesRef.current) {
      // Parent fed our own emitted array back as `value` — already in sync.
      lastSeededValueRef.current = value;
      return;
    }
    lastSeededValueRef.current = value;
    setInitialFiles(value && value.length > 0 ? value : EMPTY_FILES);
  }, [value, setInitialFiles]);

  // Notify parent of changes
  useEffect(() => {
    lastEmittedFilesRef.current = files;
    onChange?.(files);
  }, [files, onChange]);

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      // react-dropzone silently drops files that fail the `accept` (MIME) or
      // `maxSize` checks. Without surfacing this, the user picks a file and
      // sees nothing happen — no spinner, no error. Tell them why.
      if (fileRejections.length > 0) {
        const messages = new Set<string>();
        for (const { errors } of fileRejections) {
          for (const err of errors) {
            if (err.code === 'file-too-large') {
              messages.add(t.fileTooLarge.replace('{maxSize}', maxSizeLabel));
            } else if (err.code === 'file-invalid-type') {
              messages.add(
                t.fileInvalidType.replace('{allowed}', allowedExtLabel),
              );
            } else {
              messages.add(err.message);
            }
          }
        }
        messages.forEach((message) => toast.error(message));
      }

      if (!canAddMore) return;

      // Fixed-aspect reframe: intercept the first picked image and open the
      // crop dialog; the actual upload runs on confirm (with the crop rect).
      if (cropAspect != null) {
        const first = acceptedFiles[0];
        if (first && first.type.startsWith('image/')) {
          setCropFile(first);
          return;
        }
      }

      const filesToUpload = acceptedFiles.slice(0, maxFiles - files.length);
      filesToUpload.forEach((file) => {
        uploadFile(file, additionalData);
      });
    },
    [
      canAddMore,
      cropAspect,
      maxFiles,
      files.length,
      uploadFile,
      additionalData,
      t.fileTooLarge,
      t.fileInvalidType,
      maxSizeLabel,
      allowedExtLabel,
    ],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize: maxFileSize,
    multiple: mode === 'multiple',
    disabled: disabled || isPending || !canAddMore,
    noClick: false,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = files.findIndex((f) => f.id === active.id);
      const newIndex = files.findIndex((f) => f.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(files, oldIndex, newIndex);
      // Optimistically apply
      setInitialFiles(reordered);
      if (onReorder) {
        try {
          await onReorder(reordered);
        } catch {
          // Parent should toast; we leave the reordered state because
          // the visual feedback is more important than reverting.
        }
      }
    },
    [files, setInitialFiles, onReorder],
  );

  const accentReplaceMime = useMemo(
    // Matches the primary dropzone, whose `accept` map already carries these
    // extensions unconditionally (buildAcceptFromMimeTypes). Gating this one on
    // allowExtensionFallback would have greyed valid PDFs out of the REPLACE
    // dialog while the main dropzone still offered them — one component, two
    // answers.
    () => buildAcceptAttribute(allowedTypes),
    [allowedTypes],
  );

  const handleLightboxDelete = (file: MediaFile) => {
    deleteFile(file.id);
    setLightboxIndex(null);
  };

  const handleLightboxReplace = async (originalId: string, picked: File) => {
    // Replace = delete then upload, sequenced so a failed delete does not leave
    // the user with both the original and the replacement (two rows where they
    // expected one). Bail out of the upload if the delete fails — the delete
    // path already surfaces its own error toast.
    const idx = files.findIndex((f) => f.id === originalId);
    const deleted = await deleteFile(originalId);
    if (!deleted) return;
    uploadFile(picked, additionalData);
    if (idx >= 0) setLightboxIndex(null);
  };

  // ---- Render branches ----

  const showHeader = label || (mode === 'multiple' && maxFiles);
  const isEmpty = files.length === 0;

  return (
    <div className={cn('space-y-3', className)}>
      {showHeader && (
        <div className="flex items-baseline justify-between gap-3">
          {label ? (
            <Label className="text-sm font-medium">
              {label}
              {required && <span className="text-destructive ml-1">*</span>}
            </Label>
          ) : (
            <span />
          )}
          {mode === 'multiple' && (
            <span className="text-muted-foreground/80 font-mono text-[11px] tracking-wider tabular-nums">
              {files.length}/{maxFiles}
            </span>
          )}
        </div>
      )}

      {/* Single mode */}
      {mode === 'single' && (
        <SingleSlot
          file={files[0]}
          isUploading={isPending && isEmpty}
          isDeleting={deletingId === files[0]?.id}
          onClickThumb={() => setLightboxIndex(0)}
          onDelete={() => files[0] && deleteFile(files[0].id)}
          dropzone={{ getRootProps, getInputProps, isDragActive }}
          translations={t}
          hintText={hintText}
          disabled={disabled}
        />
      )}

      {/* Multiple mode */}
      {mode === 'multiple' && !isEmpty && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={files.map((f) => f.id)}
            strategy={rectSortingStrategy}
          >
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
              {files.map((file, idx) => (
                <SortableThumb
                  key={file.id}
                  file={file}
                  isDeleting={deletingId === file.id}
                  onClick={() => setLightboxIndex(idx)}
                  onDelete={() => deleteFile(file.id)}
                  deleteLabel={t.delete}
                />
              ))}

              {canAddMore && (
                <AddTile
                  getRootProps={getRootProps}
                  getInputProps={getInputProps}
                  isDragActive={isDragActive}
                  isPending={isPending}
                  label={t.addMore}
                  disabled={disabled}
                />
              )}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Multiple mode: empty state — full-width dropzone */}
      {mode === 'multiple' && isEmpty && (
        <FullDropzone
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          isDragActive={isDragActive}
          isPending={isPending}
          translations={t}
          hintText={hintText}
          disabled={disabled}
        />
      )}

      {/* Hint under the uploader — ONLY when files exist (no dropzone visible).
          The empty single/multiple dropzones render the hint inside themselves,
          so firing here too would print the format twice (the old
          "PDF" + "PDF, up to 10 MB" duplication). */}
      {hintText && !isEmpty && (
        <p className="text-muted-foreground text-xs">{hintText}</p>
      )}

      {/* Max files reached */}
      {mode === 'multiple' && !canAddMore && (
        <p className="text-muted-foreground text-xs">
          {t.maxFilesReached.replace('{maxFiles}', String(maxFiles))}
        </p>
      )}

      {/* Fixed-aspect reframe dialog (headshot). The original file uploads
          unchanged; the confirmed crop rect + intrinsic dims ride along as a
          `crop` JSON field on the upload's additional data. */}
      {cropAspect != null && (
        <MediaCropDialog
          file={cropFile}
          aspect={cropAspect}
          translations={customTranslations?.crop}
          onCancel={() => setCropFile(null)}
          onSkip={() => {
            const file = cropFile;
            setCropFile(null);
            // Upload without a crop rect — the public hero letterboxes the
            // full picture (never crops without an explicit rect).
            if (file) {
              uploadFile(file, additionalData);
            }
          }}
          onConfirm={(result) => {
            const file = cropFile;
            setCropFile(null);
            if (file) {
              uploadFile(file, {
                ...additionalData,
                crop: JSON.stringify(result),
              });
            }
          }}
        />
      )}

      {/* Lightbox */}
      <MediaLightbox
        files={files}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onDelete={handleLightboxDelete}
        onReplace={handleLightboxReplace}
        replaceAccept={accentReplaceMime}
        isReplacing={isPending}
        isDeleting={deletingId !== null}
        translations={t.lightbox}
      />
    </div>
  );
});

MediaUploader.displayName = 'MediaUploader';

// ─────────────────── Sub-components ───────────────────

function SingleSlot({
  file,
  isUploading,
  isDeleting,
  onClickThumb,
  onDelete,
  dropzone,
  translations,
  hintText,
  disabled,
}: {
  file: MediaFile | undefined;
  isUploading: boolean;
  isDeleting: boolean;
  onClickThumb: () => void;
  onDelete: () => void;
  dropzone: {
    getRootProps: ReturnType<typeof useDropzone>['getRootProps'];
    getInputProps: ReturnType<typeof useDropzone>['getInputProps'];
    isDragActive: boolean;
  };
  translations: typeof DEFAULT_UI_TRANSLATIONS;
  hintText?: string;
  disabled?: boolean;
}) {
  if (file) {
    return (
      <div className="flex items-start gap-4">
        <MediaThumb
          file={file}
          variant="single"
          onClick={onClickThumb}
          onDelete={onDelete}
          isDeleting={isDeleting}
          deleteLabel={translations.delete}
        />
      </div>
    );
  }

  // No file yet — filled, full-width dropzone (Direction A "Quiet Fill"): a
  // solid surface rather than a dashed box, coral only while dragging. Same
  // shape as the multiple-mode empty state, just a touch shorter.
  const { getRootProps, getInputProps, isDragActive } = dropzone;
  return (
    <div
      {...getRootProps()}
      className={cn(
        'group bg-card border-border relative flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border p-6 text-center transition-colors',
        isDragActive
          ? 'border-primary bg-primary/10'
          : 'hover:bg-card-hover hover:border-border/60',
        disabled && 'cursor-not-allowed opacity-50',
        isUploading && 'pointer-events-none opacity-60',
      )}
    >
      {/* eslint-disable-next-line kit-components/use-kit-primitives -- react-dropzone getInputProps() file input */}
      <input
        {...getInputProps()}
        aria-label={translations.uploadFileAriaLabel}
      />
      <Cloud
        className={cn(
          'size-6 transition-colors',
          isDragActive ? 'text-primary' : 'text-muted-foreground',
        )}
      />
      <p
        className={cn(
          'text-sm font-medium transition-colors',
          isDragActive && 'text-primary',
        )}
      >
        {isDragActive ? translations.dropHere : translations.dragSingle}
      </p>
      {hintText && <p className="text-muted-foreground text-xs">{hintText}</p>}
      {isUploading && (
        <div className="bg-background/70 absolute inset-0 grid place-items-center backdrop-blur-sm">
          <div className="flex flex-col items-center gap-2">
            <Spinner />
            <span className="text-xs font-medium">
              {translations.uploading}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function FullDropzone({
  getRootProps,
  getInputProps,
  isDragActive,
  isPending,
  translations,
  hintText,
  disabled,
}: {
  getRootProps: ReturnType<typeof useDropzone>['getRootProps'];
  getInputProps: ReturnType<typeof useDropzone>['getInputProps'];
  isDragActive: boolean;
  isPending: boolean;
  translations: typeof DEFAULT_UI_TRANSLATIONS;
  hintText?: string;
  disabled?: boolean;
}) {
  return (
    <div
      {...getRootProps()}
      className={cn(
        'bg-card border-border relative flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border p-10 text-center transition-colors',
        isDragActive
          ? 'border-primary bg-primary/10'
          : 'hover:bg-card-hover hover:border-border/60',
        disabled && 'cursor-not-allowed opacity-50',
        isPending && 'pointer-events-none opacity-60',
      )}
    >
      {/* eslint-disable-next-line kit-components/use-kit-primitives -- react-dropzone getInputProps() file input */}
      <input
        {...getInputProps()}
        aria-label={translations.uploadFilesAriaLabel}
      />
      <Cloud
        className={cn(
          'size-7 transition-colors',
          isDragActive ? 'text-primary' : 'text-muted-foreground',
        )}
      />
      <p
        className={cn(
          'text-center text-sm font-medium transition-colors',
          isDragActive && 'text-primary',
        )}
      >
        {isDragActive ? translations.dropHere : translations.dragMultiple}
      </p>
      {hintText && (
        <p className="text-muted-foreground text-center text-xs">{hintText}</p>
      )}
      {isPending && (
        <div className="bg-background/70 absolute inset-0 grid place-items-center rounded-lg backdrop-blur-sm">
          <div className="flex flex-col items-center gap-2">
            <Spinner />
            <span className="text-sm font-medium">
              {translations.uploading}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function AddTile({
  getRootProps,
  getInputProps,
  isDragActive,
  isPending,
  label,
  disabled,
}: {
  getRootProps: ReturnType<typeof useDropzone>['getRootProps'];
  getInputProps: ReturnType<typeof useDropzone>['getInputProps'];
  isDragActive: boolean;
  isPending: boolean;
  label: string;
  disabled?: boolean;
}) {
  return (
    <div
      {...getRootProps()}
      className={cn(
        'group bg-card border-border relative flex aspect-square cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border transition-colors',
        isDragActive
          ? 'border-primary bg-primary/10'
          : 'hover:bg-card-hover hover:border-border/60',
        disabled && 'cursor-not-allowed opacity-50',
        isPending && 'pointer-events-none opacity-60',
      )}
    >
      {/* eslint-disable-next-line kit-components/use-kit-primitives -- react-dropzone getInputProps() file input */}
      <input {...getInputProps()} aria-label={label} />
      <Plus
        className={cn(
          'size-5 transition-colors',
          isDragActive ? 'text-primary' : 'text-muted-foreground',
        )}
      />
      <span className="text-muted-foreground text-[11px] font-medium">
        {label}
      </span>
      {isPending && (
        <div className="bg-background/70 absolute inset-0 grid place-items-center rounded-md backdrop-blur-sm">
          <Spinner />
        </div>
      )}
    </div>
  );
}

function SortableThumb({
  file,
  isDeleting,
  onClick,
  onDelete,
  deleteLabel,
}: {
  file: MediaFile;
  isDeleting: boolean;
  onClick: () => void;
  onDelete: () => void;
  deleteLabel: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: file.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : undefined,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      // Drag handle = the whole tile, but click still fires for the inner thumb
      // (dnd-kit only triggers drag after the activation distance).
      {...attributes}
      {...listeners}
      className={cn(isDragging && 'shadow-lg')}
    >
      <MediaThumb
        file={file}
        onClick={onClick}
        onDelete={onDelete}
        isDeleting={isDeleting}
        deleteLabel={deleteLabel}
      />
    </div>
  );
}
